// ---------------------------------------------------------------------------
// abox-flag — feature-flag helper for the TTL <-> jsonb ABox dual-source switch
// (Phase 031 Plan 02 / D-05 / LIVEDATA-02).
//
// Originally the runtime gate for the `AboxSource` graph substrate's
// `'ttl' | 'jsonb'` selection. That substrate and its `ds-surface.tsx` call
// site are both gone; the flag survives because two live readers still need
// the same live-vs-fixture decision: `mutation-client-init.ts` (only
// instantiates the MCP mutation client when this is true) and
// `abox-app-deeplink.ts` (Gate A for the live deep-link app loader).
//
// Default = OFF / TTL. Hard-guard: VITE_E2E_MOCK === 'true' short-circuits to
// false REGARDLESS of VITE_LIVE_ABOX. This is the load-bearing D-05 constraint:
// `playwright.config.ts` webServer sets VITE_E2E_MOCK=true, so the 254-test
// e2e suite + Phase 030 harness stay on the TTL path with zero per-test config
// (no need to override the flag in every spec). Default-unset behavior keeps
// offline / dev runs on TTL as well.
//
// Shape mirrors `isKgBrowserEnabled` in src/features/design-system/kg-client.ts
// (project-established `import.meta.env.VITE_*` / default-OFF / `'1' | 'true'`
// truthy convention) with the ADDITIONAL E2E_MOCK hard guard prepended.
// ---------------------------------------------------------------------------

/**
 * Returns true when the live jsonb path should be used, false to keep the
 * offline/fixture default.
 *
 * Truth table:
 *   VITE_E2E_MOCK === 'true'                              -> false (HARD guard)
 *   VITE_LIVE_ABOX unset                                  -> false (default OFF)
 *   VITE_LIVE_ABOX === '1' | 'true' (case-insensitive)    -> true
 *   anything else (e.g. 'yes', 'on', '0', 'false')        -> false
 */
export function isLiveAboxEnabled(): boolean {
  // HARD guard FIRST — must run before reading VITE_LIVE_ABOX so a stray
  // truthy live-flag in the e2e env cannot reach the live tenant-scoped
  // backend (T-031-06 elevation-of-privilege mitigation).
  if (import.meta.env.VITE_E2E_MOCK === 'true') return false

  const raw = import.meta.env.VITE_LIVE_ABOX as string | undefined
  if (!raw) return false
  return raw === '1' || raw.toLowerCase() === 'true'
}
